import { useEffect } from 'react';

import { useQueryStates } from 'nuqs';
import { useOptimisticSearchParams } from 'nuqs/adapters/react-router/v7';
import { useForm } from 'react-hook-form';

import {
    DEFAULT_IN_WORK_OR_FINISHED_DEALS_DIRECTION,
    DEFAULT_IN_WORK_OR_FINISHED_DEALS_SORT,
} from '@crm/entities/deal';
import { parseDateRangeQuery, serializeDateRangeQuery } from '@crm/shared/lib';

import { DEALS_TABLE_ALLOW_SORT_CLEAR } from './consts';
import {
    inWorkDealsSearchParams,
    inWorkDealsSearchParamUrlKeys,
    loadInWorkDealsSearchParams,
} from './search-params';

import type {
    TInWorkOrFinishedDealsQueryParams,
    TInWorkOrFinishedDealsSortDirection,
    TInWorkOrFinishedDealsSortField,
} from '@crm/entities/deal';
import type { DateRange } from 'react-day-picker';

interface SearchFormValues {
    query: string;
}

const getDefaultSortDirection = (
    sort: TInWorkOrFinishedDealsSortField
): TInWorkOrFinishedDealsSortDirection =>
    sort === DEFAULT_IN_WORK_OR_FINISHED_DEALS_SORT
        ? DEFAULT_IN_WORK_OR_FINISHED_DEALS_DIRECTION
        : 'asc';

export const useInWorkDealsFilters = ({ limit }: { limit: number }) => {
    const [, setQueryParams] = useQueryStates(inWorkDealsSearchParams, {
        history: 'push',
        urlKeys: inWorkDealsSearchParamUrlKeys,
    });
    const optimisticSearchParams = useOptimisticSearchParams();
    const searchParams = loadInWorkDealsSearchParams(optimisticSearchParams);
    const apiSort = searchParams.sort ?? DEFAULT_IN_WORK_OR_FINISHED_DEALS_SORT;
    const apiDirection = searchParams.direction ?? getDefaultSortDirection(apiSort);
    const queryParams = {
        ...searchParams,
        direction: apiDirection,
        limit,
        sort: apiSort,
    } satisfies TInWorkOrFinishedDealsQueryParams;
    const { handleSubmit, register, reset } = useForm<SearchFormValues>({
        defaultValues: { query: queryParams.query ?? '' },
    });

    useEffect(() => {
        reset({ query: queryParams.query ?? '' });
    }, [queryParams.query, reset]);

    const onSubmit = handleSubmit(({ query }) => {
        void setQueryParams({ query: query.trim() || null, page: 1 });
    });

    const setDateRange = (
        fromKey: 'nextContactAtFrom' | 'paymentAtFrom',
        toKey: 'nextContactAtTo' | 'paymentAtTo',
        date?: DateRange
    ) => {
        const { from, to } = serializeDateRangeQuery(date);

        void setQueryParams({ [fromKey]: from, [toKey]: to, page: 1 });
    };

    const resetFilters = () => {
        reset({ query: '' });
        void setQueryParams({
            query: null,
            showCompleted: null,
            nextContactAtFrom: null,
            nextContactAtTo: null,
            paymentAtFrom: null,
            paymentAtTo: null,
            sort: null,
            direction: null,
            page: 1,
        });
    };

    const onSortChange = ({
        direction,
        sort,
    }: {
        direction: TInWorkOrFinishedDealsSortDirection | null;
        sort: TInWorkOrFinishedDealsSortField | null;
    }) => {
        void setQueryParams({
            sort,
            direction,
            page: 1,
        });
    };

    const onPageChange = (page: number) => {
        void setQueryParams({ page });
    };

    const onPageNormalize = (page: number) => {
        void setQueryParams({ page }, { history: 'replace' });
    };

    const hasActiveFilters = Boolean(
        queryParams.query ||
            queryParams.showCompleted ||
            queryParams.nextContactAtFrom ||
            queryParams.nextContactAtTo ||
            queryParams.paymentAtFrom ||
            queryParams.paymentAtTo
    );

    return {
        hasActiveFilters,
        paginationProps: {
            onPageChange,
            onPageNormalize,
        },
        queryParams,
        resetFilters,
        tableSortProps: {
            allowClear: DEALS_TABLE_ALLOW_SORT_CLEAR,
            sort: searchParams.sort,
            direction: searchParams.direction,
            onSortChange,
        },
        searchFormProps: {
            inputProps: register('query'),
            onSubmit,
        },
        dateFilterProps: {
            nextContact: {
                value: parseDateRangeQuery(
                    queryParams.nextContactAtFrom,
                    queryParams.nextContactAtTo
                ),
                onApply: (date?: DateRange) =>
                    setDateRange('nextContactAtFrom', 'nextContactAtTo', date),
            },
            nextPayment: {
                value: parseDateRangeQuery(queryParams.paymentAtFrom, queryParams.paymentAtTo),
                onApply: (date?: DateRange) => setDateRange('paymentAtFrom', 'paymentAtTo', date),
            },
        },
    };
};
